import React from 'react';
import { useLocation } from 'react-router-dom';
import { checkIfRoleIsIncluded, UserRole } from 'auth/checkRole';

export enum FeatureFlag {
  FORCE_EXTERNAL = 'forceExternal',
  ENABLE_TEMPLATES = 'enableTemplates',
  ENABLE_FEEDBACK = 'enableFeedback',
  ENABLE_CHATBOT = 'enableChatbot',
  ENABLE_SMART_SELECT = 'enableSmartSelect',
  RISK_DEBUG = 'riskDebug',
  E2E = 'e2e',
}

export const BETA_FEATURES_STORAGE_KEY = 'plant-designer-beta-features';

export const betaFeatures = [
  {
    flag: FeatureFlag.ENABLE_TEMPLATES,
    label: 'Templates',
    description: 'Create files from reusable templates.',
  },
  {
    flag: FeatureFlag.ENABLE_CHATBOT,
    label: 'PlantDesigner assistant',
    description: 'Enable the beta PlantDesigner assistant.',
  },
  {
    flag: FeatureFlag.ENABLE_SMART_SELECT,
    label: 'Smart Select',
    description: 'Enable equipment recommendations in the flowsheet editor.',
  },
  {
    flag: FeatureFlag.RISK_DEBUG,
    label: 'Detailed risk assessment',
    description: 'Show detailed values in risk assessments.',
  },
] as const;

const betaFeatureFlags = new Set<FeatureFlag>(
  betaFeatures.map(({ flag }) => flag),
);

export const canUseBetaFeatures = () =>
  checkIfRoleIsIncluded(UserRole.BETA_TESTER);

type BetaFeatureFlags = Partial<Record<FeatureFlag, boolean>>;

const BETA_FEATURES_CHANGED_EVENT = 'plant-designer-beta-features-changed';
let cachedBetaFeatures: BetaFeatureFlags = {};

const getBetaFeatureFlags = (): BetaFeatureFlags => {
  try {
    const storedFeatures = localStorage.getItem(BETA_FEATURES_STORAGE_KEY);
    cachedBetaFeatures = storedFeatures ? JSON.parse(storedFeatures) : {};
  } catch {
    // Keep the current session's choice when local storage is unavailable.
  }

  return cachedBetaFeatures;
};

export const isBetaFeatureEnabled = (flag: FeatureFlag) =>
  getBetaFeatureFlags()[flag] === true;

export const setBetaFeatureEnabled = (flag: FeatureFlag, enabled: boolean) => {
  const features = { ...getBetaFeatureFlags(), [flag]: enabled };
  cachedBetaFeatures = features;

  try {
    localStorage.setItem(BETA_FEATURES_STORAGE_KEY, JSON.stringify(features));
  } catch {
    // The in-memory setting remains active for the current page.
  }

  window.dispatchEvent(new Event(BETA_FEATURES_CHANGED_EVENT));
};

const getQueryStringFeatureValue = (search: string, flag: FeatureFlag) => {
  const params = new URLSearchParams(search);
  const value =
    params.get(flag) ??
    (flag === FeatureFlag.ENABLE_CHATBOT
      ? params.get('experimental')
      : flag === FeatureFlag.RISK_DEBUG
        ? params.get('debug')
        : undefined);
  return value === 'true' ? true : value === 'false' ? false : undefined;
};

export const getFeatureFlagValue = (flag: FeatureFlag, search: string) => {
  if (betaFeatureFlags.has(flag) && !canUseBetaFeatures()) {
    return false;
  }
  const queryStringValue = getQueryStringFeatureValue(search, flag);
  return queryStringValue ?? isBetaFeatureEnabled(flag);
};

const subscribeToBetaFeatures = (onStoreChange: () => void) => {
  window.addEventListener(BETA_FEATURES_CHANGED_EVENT, onStoreChange);
  window.addEventListener('storage', onStoreChange);

  return () => {
    window.removeEventListener(BETA_FEATURES_CHANGED_EVENT, onStoreChange);
    window.removeEventListener('storage', onStoreChange);
  };
};

export const useFeatureFlag = (flag: FeatureFlag) => {
  const { search } = useLocation();
  return React.useSyncExternalStore(
    subscribeToBetaFeatures,
    () => getFeatureFlagValue(flag, search),
    () => getFeatureFlagValue(flag, search),
  );
};

export const useActiveBetaFeatures = () => {
  const { search } = useLocation();
  const getSnapshot = () =>
    betaFeatures.some(({ flag }) => getFeatureFlagValue(flag, search));

  return React.useSyncExternalStore(
    subscribeToBetaFeatures,
    getSnapshot,
    getSnapshot,
  );
};

// Non-React utility for checking feature flags (e.g., during initialization)
export const checkFeatureFlag = (flag: FeatureFlag): boolean => {
  return getFeatureFlagValue(flag, window.location.search);
};
